import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Code2, Copy, Loader2, Search, ShieldCheck, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { widgetCatalog, widgetCategories } from "@/mocks/widgets";
import { channelCatalog } from "@/mocks/channels";
import { goAnchor } from "./app-nav";
import { t } from "@/lib/i18n";

/* Живой макет «Каталога виджетов»: слева категории, в центре карточки, справа превью.
   Виджет зависит от канала: если канала нет — карточка помечена «Нужен канал».
   Установка имитируется прямо в макете (код для вставки + копирование), сайт клиента не затрагивается. */

type Widget = {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  badge?: string;
  requires: string | null;
  greeting: string;
  chips: string[];
  extras: string[];
};

const LIST = widgetCatalog as unknown as Widget[];

// каналы, которые в демо уже подключены (WhatsApp и Telegram — в окне каналов, почта — в окне почты)
const CONNECTED = new Set<string>(["whatsapp", "telegram", "email", ...channelCatalog.filter((c) => c.connected).map((c) => c.id)]);

const ready = (w: Widget) => w.requires === null || CONNECTED.has(w.requires);

const channelName = (id: string | null) => (id ? channelCatalog.find((c) => c.id === id)?.name ?? id : "");

/** псевдо-скелет страницы сайта под виджетом: полоски вместо текста, чтобы превью читалось как «ваш сайт» */
function PageSkeleton() {
  return (
    <div className="pointer-events-none select-none px-3 pt-3" aria-hidden>
      <div className="flex items-center justify-between">
        <span className="h-2 w-10 rounded-full bg-foreground/25" />
        <span className="flex gap-1">
          <span className="h-1.5 w-6 rounded-full bg-foreground/15" />
          <span className="h-1.5 w-6 rounded-full bg-foreground/15" />
          <span className="h-1.5 w-6 rounded-full bg-foreground/15" />
        </span>
      </div>
      <div className="mt-3 h-2.5 w-3/4 rounded-full bg-foreground/20" />
      <div className="mt-1.5 h-1.5 w-1/2 rounded-full bg-foreground/10" />
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        <span className="h-10 rounded-sm bg-foreground/[.07]" />
        <span className="h-10 rounded-sm bg-foreground/[.07]" />
        <span className="h-10 rounded-sm bg-foreground/[.07]" />
      </div>
    </div>
  );
}

function WidgetsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string>(LIST[0]?.id ?? "");
  const [greeting, setGreeting] = useState(LIST[0]?.greeting ?? "");
  const [pos, setPos] = useState<"right" | "left">("right");
  const [chatOpen, setChatOpen] = useState(false);
  const [install, setInstall] = useState<Widget | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [installed, setInstalled] = useState<string[]>([]);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LIST.filter((w) => {
      if (cat !== "all" && w.category !== cat) return false;
      if (!q) return true;
      return `${w.name} ${w.description}`.toLowerCase().includes(q);
    });
  }, [cat, query]);

  const active = LIST.find((w) => w.id === activeId) ?? LIST[0];
  const activeReady = active ? ready(active) : false;

  const pick = (w: Widget) => {
    setActiveId(w.id);
    setGreeting(w.greeting);
    setChatOpen(false);
  };

  const blocked = LIST.filter((w) => !ready(w)).length;
  const countIn = (id: string) => (id === "all" ? LIST.length : LIST.filter((w) => w.category === id).length);

  const snippet = (w: Widget) =>
    `<script src="https://widget.hub.example/v1/loader.js"\n  data-widget="${w.id}"\n  data-position="${pos}"\n  async></script>`;

  const copy = async () => {
    if (!install) return;
    try {
      await navigator.clipboard.writeText(snippet(install));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const confirmInstall = () => {
    if (!install) return;
    setBusy(true);
    const target = install;
    setTimeout(() => {
      setInstalled((prev) => (prev.includes(target.id) ? prev : [...prev, target.id]));
      setBusy(false);
      setInstall(null);
      setCopied(false);
      flash(t("{name}: код установлен — виджет появится на сайте через минуту", { name: target.name }));
    }, 600);
  };

  return (
    <Band id="widgets" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("настройки → виджеты")}</span>
          <span className="ms-auto flex items-center gap-2">
            <span className="hidden items-center gap-1.5 whitespace-nowrap text-[11px] text-muted-foreground sm:inline-flex">
              <span className="size-1.5 rounded-full bg-emerald-400" aria-hidden />
              {t("Установлено")} {installed.length}
            </span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground">
              <i className="ri-add-line" aria-hidden />
              {t("Создать виджет")}
            </span>
          </span>
        </div>

        <div className="grid lg:grid-cols-[13.5rem_minmax(0,1fr)]">
          {/* ── категории ── */}
          <div className="hidden flex-col gap-1 border-border p-3 lg:flex lg:border-e">
            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Каталог виджетов")}</p>
            {widgetCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                aria-pressed={cat === c.id}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[13px] font-medium transition",
                  cat === c.id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(c.icon, "text-base leading-none")} aria-hidden />
                <span className="flex-1 truncate">{c.label}</span>
                <span className="text-[11px] tabular-nums opacity-70">{countIn(c.id)}</span>
              </button>
            ))}
            <div className="mt-3 rounded-btn border border-border bg-background/50 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Код виджета и ключи видит только администратор. Продавцы получают диалоги, а не токены.")}
              </p>
            </div>
          </div>

          {/* ── карточки + превью ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-col gap-3">
              <div className="flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
                {widgetCategories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCat(c.id)}
                    aria-pressed={cat === c.id}
                    className={cn(
                      "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] font-semibold transition",
                      cat === c.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <i className={c.icon} aria-hidden />
                    {c.label}
                  </button>
                ))}
              </div>

              <label className="relative block min-w-0">
                <span className="sr-only">{t("Поиск по виджетам")}</span>
                <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Найти виджет: WhatsApp, соцсети, заявка…")}
                  className="h-10 w-full box-border rounded-btn border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </label>

              {blocked > 0 ? (
                <div className="flex flex-wrap items-start gap-2.5 rounded-btn border border-amber-400/40 bg-amber-400/10 px-3.5 py-2.5">
                  <i className="ri-error-warning-line mt-0.5 text-base leading-none text-amber-500" aria-hidden />
                  <p className="min-w-0 flex-1 text-[12px] leading-relaxed text-foreground/85">
                    {t("{n} виджетов ждут подключения канала.", { n: blocked })}{" "}
                    <a href="#channels" className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary">
                      {t("Подключить канал")}
                    </a>
                  </p>
                </div>
              ) : null}
            </div>

            <div className="mt-4 grid gap-5 xl:grid-cols-[minmax(0,1fr)_16.5rem]">
              <div className="min-w-0">
                <div className="grid gap-3 sm:grid-cols-2">
                  {visible.map((w) => {
                    const ok = ready(w);
                    const isActive = w.id === active?.id;
                    const isOn = installed.includes(w.id);
                    return (
                      <article
                        key={w.id}
                        onClick={() => pick(w)}
                        className={cn(
                          "flex cursor-pointer flex-col gap-2.5 rounded-card border p-3.5 transition",
                          isActive ? "border-primary bg-primary/5" : "border-border bg-background/40 hover:border-primary/40",
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span
                            className={cn(
                              "grid size-9 flex-none place-items-center rounded-btn text-lg leading-none",
                              ok ? "bg-primary/10 text-primary" : "bg-foreground/[.06] text-muted-foreground",
                            )}
                            aria-hidden
                          >
                            <i className={w.icon} />
                          </span>
                          <span className="flex flex-wrap items-center justify-end gap-1">
                            {w.badge ? (
                              <span className="rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground ring-1 ring-border">{w.badge}</span>
                            ) : null}
                            {isOn ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                <Check className="size-2.5" strokeWidth={3} aria-hidden />
                                {t("На сайте")}
                              </span>
                            ) : null}
                          </span>
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold leading-snug">{w.name}</h3>
                          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{w.description}</p>
                        </div>

                        <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/60 pt-2.5">
                          <span className={cn("min-w-0 truncate text-[11px]", ok ? "text-muted-foreground" : "text-amber-500")}>
                            {ok ? t("Готов к установке") : t("Нужен канал: {channel}", { channel: channelName(w.requires) })}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              pick(w);
                              if (!ok) {
                                goAnchor("#channels");
                                return;
                              }
                              setInstall(w);
                              setCopied(false);
                            }}
                            className={cn(
                              "flex-none cursor-pointer whitespace-nowrap rounded-btn px-3 py-1.5 text-[11px] font-semibold transition",
                              ok ? "bg-primary text-primary-foreground hover:brightness-110" : "border border-border text-muted-foreground hover:text-foreground",
                            )}
                          >
                            {ok ? (isOn ? t("Код") : t("Установить")) : t("Подключить")}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
                {visible.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">{t("Виджет не найден. Попробуйте другое название.")}</p> : null}
              </div>

              {/* ── превью ── */}
              <aside className="min-w-0 xl:border-s xl:border-border xl:ps-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Превью")}</p>

                <div className="mt-2 overflow-hidden rounded-btn border border-border bg-background">
                  <div className="flex items-center gap-1.5 border-b border-border bg-muted/60 px-2.5 py-2" aria-hidden>
                    <span className="size-2 rounded-full bg-foreground/20" />
                    <span className="size-2 rounded-full bg-foreground/20" />
                    <span className="size-2 rounded-full bg-foreground/20" />
                    <span className="ms-1 h-2 w-16 rounded-full bg-foreground/10" />
                  </div>
                  <div className="relative h-[268px] bg-background">
                    <PageSkeleton />
                    <div className={cn("absolute bottom-3 z-10", pos === "right" ? "end-3" : "start-3")}>
                      {chatOpen && active ? (
                        <div className="w-[186px] overflow-hidden rounded-btn border border-border bg-card shadow-lift">
                          <div className="flex items-center gap-2 border-b border-border bg-primary px-2.5 py-2 text-primary-foreground">
                            <i className={cn(active.icon, "text-sm leading-none")} aria-hidden />
                            <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">{str(brand) || "Hub"}</span>
                            <button type="button" onClick={() => setChatOpen(false)} aria-label={t("Свернуть превью чата")} className="cursor-pointer opacity-80 hover:opacity-100">
                              <X className="size-3" aria-hidden />
                            </button>
                          </div>
                          <div className="p-2">
                            <p className="rounded-btn rounded-ss-none bg-muted px-2 py-1.5 text-[10px] leading-snug text-foreground">{greeting || active.greeting}</p>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {active.chips.slice(0, 3).map((c) => (
                                <span key={c} className="rounded-full border border-primary/40 px-1.5 py-0.5 text-[9px] font-medium text-primary">
                                  {c}
                                </span>
                              ))}
                            </div>
                            <div className="mt-1.5 flex items-center gap-1 rounded-btn border border-border bg-background px-1.5 py-1">
                              <span className="text-[9px] text-muted-foreground">{t("Сообщение…")}</span>
                              <i className="ri-send-plane-2-fill ms-auto text-[10px] leading-none text-primary" aria-hidden />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setChatOpen(true);
                            if (active) setGreeting(active.greeting);
                          }}
                          aria-label={t("Открыть превью виджета")}
                          className={cn(
                            "grid size-11 cursor-pointer place-items-center rounded-full text-xl leading-none shadow-lift transition hover:brightness-110",
                            activeReady ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground ring-1 ring-border",
                          )}
                        >
                          <i className={active ? active.icon : "ri-chat-3-line"} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid gap-2.5">
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Позиция")}</span>
                    <div className="mt-1.5 flex gap-1 rounded-full border border-border bg-background p-1">
                      {(["left", "right"] as const).map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPos(p)}
                          aria-pressed={pos === p}
                          className={cn(
                            "flex-1 cursor-pointer whitespace-nowrap rounded-full px-2 py-1 text-[11px] font-semibold transition",
                            pos === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {p === "left" ? t("Слева") : t("Справа")}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="widget-greeting" className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("Приветствие")}
                    </label>
                    <input
                      id="widget-greeting"
                      value={greeting}
                      onChange={(e) => setGreeting(e.target.value)}
                      maxLength={120}
                      placeholder={active?.greeting}
                      className="mt-1.5 h-9 w-full box-border rounded-btn border border-border bg-background px-2.5 text-[11px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!active) return;
                      if (!activeReady) {
                        goAnchor("#channels");
                        return;
                      }
                      setInstall(active);
                      setCopied(false);
                    }}
                    className={cn(
                      "inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn text-[12px] font-semibold transition",
                      activeReady
                        ? "bg-primary text-primary-foreground hover:brightness-110"
                        : "border border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Code2 className="size-3.5" aria-hidden />
                    {activeReady ? t("Код для вставки") : t("Сначала канал")}
                  </button>
                  <p className="text-[10px] leading-relaxed text-muted-foreground">
                    {activeReady
                      ? t("Превью настоящее: двигайте позицию и текст — так виджет и будет выглядеть на сайте.")
                      : t("Этот виджет пока не во что писать — подключите канал, и превью оживёт.")}
                  </p>
                </div>
              </aside>
            </div>

            <p className="mt-4 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              {t("Демо-режим: установка имитируется в макете, код не публикуется и сайт не затрагивает.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-30 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окно установки ── */}
        {install ? (
          <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="widget-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setInstall(null)}
                aria-label={t("Закрыть окно установки")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className={install.icon} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Код для сайта")}</p>
                  <h3 id="widget-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {t("Установить «{name}»", { name: install.name })}
                  </h3>
                </div>
              </div>

              <ol className="mt-4 flex flex-col gap-2.5">
                {[
                  t("Скопируйте код кнопкой ниже"),
                  t("Вставьте его перед закрывающим тегом </body> на сайте"),
                  t("Обновите страницу — кнопка появится в правом нижнем углу"),
                ].map((s, i) => (
                  <li key={s} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-muted-foreground">
                    <span className="mt-0.5 grid size-5 flex-none place-items-center rounded-full bg-primary/15 text-[10px] font-bold text-primary tabular-nums" aria-hidden>
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>

              <div className="mt-4 rounded-btn border border-border bg-background/60 p-3.5">
                <div className="flex items-center justify-between gap-2 pb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">HTML</span>
                  <button
                    type="button"
                    onClick={copy}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold text-muted-foreground transition hover:text-foreground"
                  >
                    {copied ? <Check className="size-3.5 text-primary" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
                    {copied ? t("Скопировано") : t("Копировать")}
                  </button>
                </div>
                <pre className="overflow-x-auto rounded-btn bg-muted px-2.5 py-2 font-mono text-[11px] leading-relaxed text-foreground">{snippet(install)}</pre>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {t("Позиция: {pos} · приветствие: «{greeting}»", { pos: pos === "right" ? t("справа") : t("слева"), greeting: greeting || install.greeting })}
                </p>
              </div>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setInstall(null)}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="button"
                  onClick={confirmInstall}
                  disabled={busy}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                  {busy ? t("Устанавливаем…") : t("Отметить как установленный")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default WidgetsDemo;